// Standard furniture, placed the way a first sketch places it: against a
// wall, clear of every door's swing, with room in front to use it.
//
// It is not decoration. A bedroom that cannot take a 1.60 bed and a wardrobe,
// or a bathroom with no wall for a bath, is a plan problem the area figure
// hides, and furnishing every room is how an architect finds it. So a piece
// that does not fit is reported, not squeezed in.
//
// Every piece is a rectangle in the frame of the wall it stands on: `o` is
// its corner on the wall, `u` runs along the wall for `w`, `n` points into
// the room for `d`. Pieces that stand free (a dining table) use the same
// frame with `o` off the wall. All tests are plain vector maths — rooms are
// a handful of edges and this runs for one flat at a time, on demand.
import { distanceTo, type MultiPoly, type Pt } from "./geom.ts";
import type { Opening } from "./openings.ts";
import type { Room, Unit } from "./layout.ts";

export type FurnitureKind =
  | "bedDouble" | "bedSingle" | "wardrobe" | "desk"
  | "sofa" | "diningTable" | "counter"
  | "bathtub" | "shower" | "wc" | "basin" | "washer"
  | "hallUnit" | "shelves";

export interface Furniture {
  kind: FurnitureKind;
  /** Index into the flat's rooms. */
  room: number;
  o: Pt;
  u: Pt;
  n: Pt;
  w: number;
  d: number;
}

export interface Furnished {
  pieces: Furniture[];
  /** What a room should take and does not: "Dormitor 2: pat dublu". */
  missing: { room: number; kind: FurnitureKind }[];
}

interface Spec {
  kind: FurnitureKind;
  /** Widths to try along the wall, largest first. */
  w: number[];
  d: number;
  /** Free floor needed in front of it to use it. */
  clear: number;
  /** Tall pieces may not stand in front of a window. */
  tall?: boolean;
  /** Prefer a wall with no window on it (a bedhead, a sofa back, a counter). */
  solidWall?: boolean;
  /** Stands in the room, not on a wall. */
  free?: boolean;
  /** Not having it is not worth reporting. */
  optional?: boolean;
}

const BED_DOUBLE: Spec = { kind: "bedDouble", w: [1.6, 1.4], d: 2.0, clear: 0.6, solidWall: true };
const BED_SINGLE: Spec = { kind: "bedSingle", w: [0.9], d: 2.0, clear: 0.6, solidWall: true };
const WARDROBE: Spec = { kind: "wardrobe", w: [2.4, 2.0, 1.6, 1.2, 1.0], d: 0.6, clear: 0.7, tall: true };
const DESK: Spec = { kind: "desk", w: [1.2, 1.0], d: 0.6, clear: 0.7, optional: true };
const SOFA: Spec = { kind: "sofa", w: [2.4, 2.1, 1.8], d: 0.9, clear: 1.0, solidWall: true };
const DINING: Spec = { kind: "diningTable", w: [1.6, 1.2, 0.9], d: 0.9, clear: 0, free: true };
const COUNTER: Spec = { kind: "counter", w: [3.6, 3.0, 2.4, 1.8], d: 0.6, clear: 1.0, solidWall: true };
const BATH: Spec = { kind: "bathtub", w: [1.7, 1.6], d: 0.75, clear: 0.6, solidWall: true };
const SHOWER: Spec = { kind: "shower", w: [0.9, 0.8], d: 0.8, clear: 0.55 };
const WC: Spec = { kind: "wc", w: [0.4], d: 0.65, clear: 0.6, solidWall: true };
const BASIN: Spec = { kind: "basin", w: [0.6, 0.5, 0.45], d: 0.4, clear: 0.55 };
const WASHER: Spec = { kind: "washer", w: [0.6], d: 0.6, clear: 0.6, optional: true };
const HALL_UNIT: Spec = { kind: "hallUnit", w: [1.6, 1.2, 0.8], d: 0.4, clear: 0.8, tall: true, optional: true };
const SHELVES: Spec = { kind: "shelves", w: [2.0, 1.4, 1.0, 0.6], d: 0.4, clear: 0.6, optional: true };

const add = (a: Pt, b: Pt): Pt => [a[0] + b[0], a[1] + b[1]];
const mul = (a: Pt, k: number): Pt => [a[0] * k, a[1] * k];
const dot = (a: Pt, b: Pt) => a[0] * b[0] + a[1] * b[1];

/** The four corners of a piece, or of the strip `depth` deep in front of it. */
export function corners(f: Pick<Furniture, "o" | "u" | "n" | "w" | "d">, from = 0, depth = f.d): Pt[] {
  const a = add(f.o, mul(f.n, from));
  return [a, add(a, mul(f.u, f.w)), add(add(a, mul(f.u, f.w)), mul(f.n, depth)), add(a, mul(f.n, depth))];
}

/** Two convex quads overlap by more than `tol` (separating-axis test). */
function overlap(a: Pt[], b: Pt[], tol = 0.01): boolean {
  for (const poly of [a, b]) for (let i = 0; i < poly.length; i++) {
    const p = poly[i], q = poly[(i + 1) % poly.length];
    const axis: Pt = [-(q[1] - p[1]), q[0] - p[0]];
    const len = Math.hypot(axis[0], axis[1]) || 1;
    const ax: Pt = [axis[0] / len, axis[1] / len];
    const pa = a.map((x) => dot(x, ax)), pb = b.map((x) => dot(x, ax));
    if (Math.max(...pa) <= Math.min(...pb) + tol || Math.max(...pb) <= Math.min(...pa) + tol) return false;
  }
  return true;
}

function insideQuad(p: Pt, q: Pt[], margin = 0.02): boolean {
  for (let i = 0; i < 4; i++) {
    const a = q[i], b = q[(i + 1) % 4];
    const cross = (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    if (cross / len < margin) return false; // corners are wound counter-clockwise
  }
  return true;
}

const ccw = (q: Pt[]) => {
  const s = q.reduce((t, p, i) => { const r = q[(i + 1) % q.length]; return t + p[0] * r[1] - r[0] * p[1]; }, 0);
  return s >= 0 ? q : [...q].reverse();
};

/** A quad lies in the room: corners and edge midpoints inside, and no corner of the room pokes into it. */
function fits(q: Pt[], room: MultiPoly): boolean {
  const probe = q.flatMap((p, i) => { const r = q[(i + 1) % 4]; return [p, [(p[0] + r[0]) / 2, (p[1] + r[1]) / 2] as Pt]; });
  if (probe.some((p) => distanceTo(p, room) > 0.01)) return false;
  const cq = ccw(q);
  return !room.some((poly) => poly.some((ring) => ring.some((v) => insideQuad(v, cq))));
}

/** Openings on a room's outline, with their keep-out squares. */
function openingsOf(room: MultiPoly, openings: Opening[]) {
  const on = openings.filter((o) => distanceToOutline2([(o.a[0] + o.b[0]) / 2, (o.a[1] + o.b[1]) / 2], room) < 0.06);
  const zone = (o: Opening, depth: number): Pt[] => {
    const L = Math.hypot(o.b[0] - o.a[0], o.b[1] - o.a[1]) || 1;
    const n: Pt = [-(o.b[1] - o.a[1]) / L, (o.b[0] - o.a[0]) / L];
    return [add(o.a, mul(n, -depth)), add(o.b, mul(n, -depth)), add(o.b, mul(n, depth)), add(o.a, mul(n, depth))];
  };
  const doors = on.filter((o) => o.kind === "door" || o.kind === "frontDoor" || o.kind === "balconyDoor")
    .map((o) => zone(o, Math.hypot(o.b[0] - o.a[0], o.b[1] - o.a[1])));
  const windows = on.filter((o) => o.kind === "window" || o.kind === "balconyDoor");
  return { doors, windows, windowZones: windows.map((o) => zone(o, 0.6)) };
}

function distanceToOutline2(p: Pt, mp: MultiPoly): number {
  let best = Infinity;
  for (const poly of mp) for (const ring of poly) for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [ax, ay] = ring[j], [bx, by] = ring[i];
    const dx = bx - ax, dy = by - ay, len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, ((p[0] - ax) * dx + (p[1] - ay) * dy) / len2)) : 0;
    best = Math.min(best, Math.hypot(ax + t * dx - p[0], ay + t * dy - p[1]));
  }
  return best;
}

interface Wall { a: Pt; u: Pt; n: Pt; L: number; windowed: boolean }

function walls(room: MultiPoly, windows: Opening[]): Wall[] {
  const out: Wall[] = [];
  for (const poly of room) for (const ring of poly) for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (L < 0.4) continue;
    const u: Pt = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
    let n: Pt = [-u[1], u[0]];
    const mid: Pt = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    if (distanceTo(add(mid, mul(n, 0.05)), room) > 0) n = [-n[0], -n[1]];
    if (distanceTo(add(mid, mul(n, 0.05)), room) > 0) continue;
    const windowed = windows.some((o) => { const m: Pt = [(o.a[0] + o.b[0]) / 2, (o.a[1] + o.b[1]) / 2]; const t = dot([m[0] - a[0], m[1] - a[1]], u); const off = Math.abs(dot([m[0] - a[0], m[1] - a[1]], n)); return off < 0.06 && t > -0.05 && t < L + 0.05; });
    out.push({ a, u, n, L, windowed });
  }
  return out;
}

/** Furnish one flat. Rooms are taken largest bedroom first, so the double bed goes where it fits best. */
export function furnish(u: Unit): Furnished {
  const rooms: Room[] = u.rooms ?? [];
  const openings = u.openings ?? [];
  const pieces: Furniture[] = [];
  const missing: Furnished["missing"] = [];
  const hasKitchen = rooms.some((r) => r.kind === "kitchen");
  const roomArea = (r: Room) => r.polygon.reduce((t, p) => t + Math.abs(p[0].reduce((s, q, i) => { const z = p[0][(i + 1) % p[0].length]; return s + q[0] * z[1] - z[0] * q[1]; }, 0) / 2), 0);
  const bedrooms = rooms.map((r, i) => [r, i] as const).filter(([r]) => r.kind === "bedroom").sort((a, b) => roomArea(b[0]) - roomArea(a[0]));
  const master = Number(u.type ?? "1") >= 2 ? bedrooms[0]?.[1] : undefined;

  rooms.forEach((r, ri) => {
    const { doors, windows, windowZones } = openingsOf(r.polygon, openings);
    const ws = walls(r.polygon, windows);
    const bodies: Pt[][] = [], clears: Pt[][] = [];

    const ok = (body: Pt[], clear: Pt[] | undefined, s: Spec) => {
      if (!fits(body, r.polygon)) return false;
      if (clear && !fits(clear, r.polygon)) return false;
      if (doors.some((z) => overlap(body, z))) return false;
      if (s.tall && windowZones.some((z) => overlap(body, z))) return false;
      if (bodies.some((b) => overlap(body, b) || (clear && overlap(clear, b)))) return false;
      if (clears.some((c) => overlap(body, c))) return false;
      return true;
    };
    const keep = (f: Furniture, s: Spec) => {
      pieces.push(f);
      bodies.push(corners(f));
      if (s.clear) clears.push(corners(f, f.d, s.clear));
      return f;
    };

    const onWall = (s: Spec): Furniture | undefined => {
      const order = [...ws].sort((a, b) => (s.solidWall ? Number(a.windowed) - Number(b.windowed) : 0) || b.L - a.L);
      for (const w of s.w) for (const wall of order) {
        if (wall.L < w) continue;
        const span = wall.L - w;
        const at = [0, span, span / 2, ...Array.from({ length: Math.floor(span / 0.2) }, (_, k) => (k + 1) * 0.2)];
        for (const t of at) {
          const f: Furniture = { kind: s.kind, room: ri, o: add(wall.a, mul(wall.u, t)), u: wall.u, n: wall.n, w, d: s.d };
          if (ok(corners(f), s.clear ? corners(f, f.d, s.clear) : undefined, s)) return keep(f, s);
        }
      }
      return undefined;
    };

    const standing = (s: Spec): Furniture | undefined => {
      // the room's own frame: its longest wall; candidates nearest the middle first
      const wall = [...ws].sort((a, b) => b.L - a.L)[0];
      if (!wall) return undefined;
      const pts = r.polygon.flatMap((p) => p[0]);
      const c: Pt = [pts.reduce((t, p) => t + p[0], 0) / pts.length, pts.reduce((t, p) => t + p[1], 0) / pts.length];
      const chairs = 0.5; // chairs pulled out, all round
      const cands: Pt[] = [];
      for (let i = -12; i <= 12; i++) for (let j = -12; j <= 12; j++) cands.push(add(c, add(mul(wall.u, i * 0.2), mul(wall.n, j * 0.2))));
      cands.sort((p, q) => Math.hypot(p[0] - c[0], p[1] - c[1]) - Math.hypot(q[0] - c[0], q[1] - c[1]));
      for (const w of s.w) for (const [uu, nn, ww, dd] of [[wall.u, wall.n, w, s.d], [wall.n, [-wall.u[0], -wall.u[1]] as Pt, w, s.d]] as [Pt, Pt, number, number][]) for (const p of cands) {
        const f: Furniture = { kind: s.kind, room: ri, o: add(p, add(mul(uu, -ww / 2), mul(nn, -dd / 2))), u: uu, n: nn, w: ww, d: dd };
        const zone = { ...f, o: add(f.o, add(mul(uu, -chairs), mul(nn, -chairs))), w: ww + 2 * chairs, d: dd + 2 * chairs };
        // the chairs need the room and no furniture under them, but may share
        // the free floor in front of a sofa or a counter; the table may not
        const z = corners(zone), t = corners(f);
        if (!fits(z, r.polygon) || doors.some((q) => overlap(z, q)) || bodies.some((b) => overlap(z, b)) || clears.some((c) => overlap(t, c))) continue;
        pieces.push(f); bodies.push(z); return f;
      }
      return undefined;
    };

    const place = (s: Spec, fallback?: Spec) => {
      const f = s.free ? standing(s) : onWall(s);
      if (f) return f;
      if (fallback) return place(fallback);
      if (!s.optional) missing.push({ room: ri, kind: s.kind });
      return undefined;
    };

    switch (r.kind) {
      case "bedroom":
        if (ri === master) place(BED_DOUBLE, BED_SINGLE); else place(BED_SINGLE);
        place(WARDROBE);
        if (ri !== master) place(DESK);
        break;
      case "living":
        if (!hasKitchen) place(COUNTER);
        place(SOFA);
        place({ ...DINING, optional: roomArea(r) < 16 });
        break;
      case "kitchen":
        place(COUNTER);
        place({ ...DINING, w: [1.2, 0.9, 0.8], d: 0.8, optional: true });
        break;
      case "bath": {
        // a bath if WC and basin still fit beside it, else a shower
        const mark = [pieces.length, bodies.length, clears.length, missing.length];
        place(BATH); place(WC); place(BASIN);
        if (missing.length > mark[3]) {
          pieces.length = mark[0]; bodies.length = mark[1]; clears.length = mark[2]; missing.length = mark[3];
          place(SHOWER); place(WC); place(BASIN);
        }
        place(WASHER);
        break;
      }
      case "hall":
        place(HALL_UNIT);
        break;
      case "storage":
        place(SHELVES);
        break;
    }
  });
  return { pieces, missing };
}

export const FURNITURE_NAME: Record<FurnitureKind, string> = {
  bedDouble: "pat dublu", bedSingle: "pat simplu", wardrobe: "dulap", desk: "birou",
  sofa: "canapea", diningTable: "masă", counter: "bucătărie",
  bathtub: "cadă", shower: "duș", wc: "WC", basin: "lavoar", washer: "mașină de spălat",
  hallUnit: "cuier", shelves: "rafturi",
};
